import React, { useState, useEffect, FC } from 'react';

interface TimePickerProps {
  onChange: (time: string) => void;
  initialTime?: string; // Expected in "HH:MM AM/PM" format
  dataCy?: string;
}

const TimePicker: FC<TimePickerProps> = ({ onChange, initialTime = '12:00 AM', dataCy = '' }) => {
  const [hour, setHour] = useState<string>(initialTime.split(':')[0]);
  const [minute, setMinute] = useState<string>(initialTime.split(':')[1].split(' ')[0]);
  const [period, setPeriod] = useState<string>(initialTime.split(' ')[1]); // "AM" or "PM"

  useEffect(() => {
    // Convert 12-hour time format to 24-hour format for ISO 8601 compatibility
    const hours24 = hour === '12' ? '00' : hour.padStart(2, '0');
    const adjustedHour =
      period === 'PM' ? (parseInt(hours24, 10) % 12) + 12 : parseInt(hours24, 10);
    const isoTime = `${adjustedHour.toString().padStart(2, '0')}:${minute}`;

    onChange?.(isoTime);
  }, [hour, minute, period, onChange]);

  const generateOptions = (start: number, end: number): string[] => {
    const options: string[] = [];
    for (let i = start; i <= end; i++) {
      options.push(i < 10 ? `0${i}` : `${i}`);
    }
    return options;
  };

  return (
    <div className='flex items-center gap-2' data-cy={dataCy}>
      <select
        value={hour}
        onChange={(e) => setHour(e.target.value)}
        className='block h-8 py-0 pl-3 m-0 transition ease-in-out bg-no-repeat border border-solid rounded appearance-none border-shadow pr-7 w-13 form-select typography-body text-ui bg-clip-padding focus:text-gray-700 focus:bg-white focus:border-ocean-blue-500 focus:outline-none'
        data-cy={dataCy ? `${dataCy}-hours` : ''}
      >
        {generateOptions(1, 12).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <span className='text-gray-700 typography-body'>:</span>
      <select
        value={minute}
        onChange={(e) => setMinute(e.target.value)}
        className='block h-8 py-0 pl-3 m-0 transition ease-in-out bg-no-repeat border border-solid rounded appearance-none border-ui pr-7 w-13 form-select typography-body text-ui bg-clip-padding focus:text-gray-700 focus:bg-white focus:border-ocean-blue-500 focus:outline-none'
        data-cy={dataCy ? `${dataCy}-minutes` : ''}
      >
        {[0, 15, 30, 45].map((opt) => (
          <option key={opt} value={opt < 10 ? `0${opt}` : `${opt}`}>
            {opt < 10 ? `0${opt}` : `${opt}`}
          </option>
        ))}
      </select>
      <select
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        className='block h-8 py-0 pl-3 m-0 transition ease-in-out bg-no-repeat border border-solid rounded appearance-none border-ui pr-7 w-13 form-select typography-body text-ui bg-clip-padding focus:text-gray-700 focus:bg-white focus:border-ocean-blue-500 focus:outline-none'
        data-cy={dataCy ? `${dataCy}-period` : ''}
      >
        <option value='AM'>AM</option>
        <option value='PM'>PM</option>
      </select>
    </div>
  );
};

export default TimePicker;
